import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    CleaningServices,
    AttachMoney,
    PeopleAlt,
    Home,
    EventAvailable,
    Assignment,
    CheckCircle,
    Gavel,
    LocalLaundryService,
    PersonAdd,
    DeleteOutline,
    FormatListBulleted,
    InfoOutlined,
    Group,
    ChevronRight,
    ShoppingCart
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアでルールが大切な理由' },
    { id: 'basic_rules', label: 'ルームシェア必須ルール10選' },
    { id: 'cleaning', label: '掃除・ゴミ出し分担の決め方' },
    { id: 'costs', label: '費用分担のベストプラクティス' },
    { id: 'guests', label: '来客・宿泊ルールをどう決める？' },
    { id: 'document_template', label: 'ルール文書化の雛形とポイント' },
    { id: 'troubles', label: 'トラブルを防ぐためのコツ' },
    { id: 'conclusion', label: 'まとめ・円満なルームシェアのために' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem
                                    key={item.id}
                                    sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.05rem'
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '0.97rem',
                                            textAlign: 'left',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアでルールが大切な理由
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、さまざまなライフスタイルに合わせて<span style={{ fontWeight: 'bold' }}>ルームシェア</span>を選ぶ人が増えています。家賃や光熱費を抑えながら、他者と暮らすことで新しい発見や人間関係を築くことができる一方、<span style={{ color: '#1976d2', fontWeight: 'bold' }}>思わぬトラブル</span>に悩むケースも少なくありません。<br />
                    <span style={{ fontWeight: 500 }}>「掃除のやり方や頻度が合わない」「光熱費の精算をどうするか揉めた」「友人の宿泊をめぐって不満が溜まった」</span>など、生活の細かな部分が原因で関係がギクシャクすることも…。<br />
                    そこで<strong>ルームシェア成功の鍵</strong>となるのが、<span style={{ background: '#fffde7', padding: '0 2px', borderRadius: 1 }}>最初にしっかりとルールを決めておくこと</span>。本記事では、<Chip label="掃除・ゴミ出し分担" color="primary" size="small" sx={{ mx: 0.5 }} />や<Chip label="費用分担" color="primary" size="small" sx={{ mx: 0.5 }} />、<Chip label="来客・宿泊ルール" color="primary" size="small" sx={{ mx: 0.5 }} />など、<strong>トラブルを未然に防ぐためのルール作り</strong>について徹底解説します。
                </Typography>
                <Box sx={{ mb: 3, mt: 2 }}>
                    <img
                        src="/images/blog/living-dining_roomshare-rule-guide.jpg"
                        alt="明るい共有リビングでルームメイトたちが食事や談笑を楽しみ、リラックスした雰囲気が伝わるルームシェアのシーン。木目調のテーブルと観葉植物がアクセントとなり、自然光がたっぷり入る温かみのある空間。"
                        style={{
                            width: '100%',
                            maxHeight: 300,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee'
                        }}
                    />
                </Box>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    ルームシェア生活を快適に、そして長く続けるために、今すぐ実践できるノウハウをこの一記事でまとめました。
                </Typography>
            </Box>

            {/* ルームシェア必須ルール10選 */}
            <Box id="basic_rules" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア必須ルール10選
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは、<strong>「あたりまえ」</strong>が人によって異なるもの。<Chip label="必須ルール" color="primary" size="small" sx={{ mx: 0.5 }} />をしっかり決めることで、<span style={{ color: '#1976d2' }}>小さなすれ違いも未然に防げます。</span>
                </Typography>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: '#fafafa' }} elevation={1}>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CleaningServices color="primary" /></ListItemIcon>
                            <ListItemText primary="掃除・片付けの分担ルール" secondary="共用部や自室の掃除頻度・担当を明確に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><DeleteOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="ゴミ出し・分別の担当" secondary="曜日や分別ルール、当番の決め方" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><AttachMoney color="primary" /></ListItemIcon>
                            <ListItemText primary="家賃・光熱費など費用分担" secondary="精算方法や支払いスケジュール" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PeopleAlt color="primary" /></ListItemIcon>
                            <ListItemText primary="来客・宿泊のルール" secondary="友人や家族の訪問・宿泊の範囲と連絡方法" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Home color="primary" /></ListItemIcon>
                            <ListItemText primary="共有スペース使用のマナー" secondary="キッチンやバスルーム利用のルール化" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><LocalLaundryService color="primary" /></ListItemIcon>
                            <ListItemText primary="洗濯・乾燥機の利用順序" secondary="予約や利用時間帯の調整" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EventAvailable color="primary" /></ListItemIcon>
                            <ListItemText primary="生活リズムの共有" secondary="起床・就寝時間、シャワー利用時間の調整" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Assignment color="primary" /></ListItemIcon>
                            <ListItemText primary="ルールの文書化・共有" secondary="変更時の合意・周知方法を明確に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Gavel color="primary" /></ListItemIcon>
                            <ListItemText primary="トラブル時の話し合い方法" secondary="第三者の仲介や、話し合いのタイミング" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                            <ListItemText primary="プライバシー・防犯に関するルール" secondary="鍵の管理や個人情報の取り扱い" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    これらは最低限決めておきたいルールの例です。特に<strong>掃除・費用・来客</strong>の3つはマスト！
                </Typography>
            </Box>

            {/* 掃除・ゴミ出し分担の決め方 */}
            <Box id="cleaning" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    掃除・ゴミ出し分担の決め方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの「快適さ」を大きく左右するのが<strong>掃除やゴミ出し</strong>です。お互いの「きれい」の基準が違うため、<span style={{ color: '#1976d2' }}>具体的なルール化</span>が超重要！
                </Typography>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: '#e3f2fd' }} elevation={0}>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CleaningServices color="primary" /></ListItemIcon>
                            <ListItemText primary="共用部の掃除は当番制 or 役割分担" secondary="キッチン・リビング・浴室・トイレなど場所ごとに担当を決める" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><FormatListBulleted color="primary" /></ListItemIcon>
                            <ListItemText primary="掃除頻度を明記" secondary="例：週1でリビング掃除、毎日キッチンの流し掃除など" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><DeleteOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="ゴミ出し当番・分別ルール" secondary="曜日ごとに担当を分けたり、持ち回り制にする" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Assignment color="primary" /></ListItemIcon>
                            <ListItemText primary="当番表を作成・掲示" secondary="Googleカレンダーや紙で管理すると忘れ防止！" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    <strong>ポイント：</strong>「共用部は気づいた人がやる」だと不公平感が出やすい。<br />
                    明確なルールと可視化（当番表）が負担感を減らします。
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <img
                        src="/images/blog/cleaning-schedule_roomshare-rule-guide.jpg"
                        alt="リビングの壁に貼られたカラフルな掃除当番表と、掃除道具を持ったルームメイトたちが和やかに分担を話し合っている様子。協力的な雰囲気と整理された空間が印象的。"
                        style={{
                            width: '100%',
                            maxHeight: 220,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee'
                        }}
                    />
                </Box>
            </Box>

            {/* 費用分担のベストプラクティス */}
            <Box id="costs" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    費用分担のベストプラクティス
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>家賃・光熱費・日用品</strong>…。お金の話は曖昧にせず、<span style={{ color: '#1976d2', fontWeight: 'bold' }}>明確な精算ルール</span>が大切です。
                </Typography>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: '#fffde7' }} elevation={0}>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><AttachMoney color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="家賃・共益費の分担方法"
                                secondary="部屋の広さや専有部の違いに応じて公平に設定"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><AttachMoney color="success" /></ListItemIcon>
                            <ListItemText
                                primary="光熱費・インターネット"
                                secondary="毎月の請求を人数割り・実際の使用量で分ける"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ShoppingCart color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="日用品・消耗品費"
                                secondary="トイレットペーパーなど共用物は定額積立 or 購入担当をローテ"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Assignment color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="精算ルール・支払い日"
                                secondary="毎月●日までに代表者口座に振込/現金手渡しなど具体的に"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><InfoOutlined color="secondary" /></ListItemIcon>
                            <ListItemText
                                primary="家賃滞納や支払い遅延時のルール"
                                secondary="支払い遅延時の連絡方法やペナルティも事前決めが安心"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    <strong>実務的アドバイス：</strong>家計簿アプリやGoogleスプレッドシートで「誰が何をいくら払ったか」を管理するのがおすすめ。<br />
                    曖昧な分担はトラブルのもと。<span style={{ color: '#1976d2' }}>金額と期限を明示</span>しておきましょう。
                </Typography>
            </Box>

            {/* 来客・宿泊ルール */}
            <Box id="guests" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    来客・宿泊ルールをどう決める？
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで最もトラブルが起きやすいのが<strong>「来客・宿泊」</strong>に関する部分。お互いのプライバシーや安全のため、<Chip label="事前連絡" color="primary" size="small" sx={{ mx: 0.5 }} />と<Chip label="ルール化" color="primary" size="small" sx={{ mx: 0.5 }} />は必須です。
                </Typography>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: '#e8f5e9' }} elevation={0}>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><PersonAdd color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="来客は事前にLINEや掲示板で連絡"
                                secondary="連絡なしの友人訪問は原則NG"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EventAvailable color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="宿泊ルール"
                                secondary="家族のみOK／同性友人は月○回まで／異性は宿泊不可、など具体的に"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Home color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="共用部の利用時間・エリア"
                                secondary="来客はリビングは21時まで、深夜帯の利用禁止など"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="防犯・安全面の配慮"
                                secondary="来客に鍵を貸さない、入退室は必ず同伴など"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    <strong>チェックポイント：</strong>「お互い様」で済ませず、必ず「事前連絡」と「頻度制限」を設定しましょう。
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <img
                        src="/images/blog/guest-discussion_roomshare-rule-guide.jpg"
                        alt="リビングのソファでルームメイトたちが来客・宿泊ルールについて真剣に話し合い、ホワイトボードに意見を書き出している場面。多様な意見を尊重し合う雰囲気が伝わる。"
                        style={{
                            width: '100%',
                            maxHeight: 220,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee'
                        }}
                    />
                </Box>
            </Box>

            {/* ルール文書化の雛形とポイント */}
            <Box id="document_template" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルール文書化の雛形とポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    せっかく決めたルールも、<strong>「忘れてた」「そんなルール知らなかった」</strong>では意味がありません。<span style={{ color: '#1976d2' }}>文書化＆見える化</span>で全員の安心感アップ！
                </Typography>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, background: '#fce4ec' }} elevation={0}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#ad1457' }}>
                        ルームシェアルール文書の雛形（例）
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>第1条（掃除・ゴミ出し）</strong><br />
                        ・共用スペースの掃除は週1回、当番表に従って行う。<br />
                        ・ゴミ出しは曜日ごとに担当を決め、分別を守ること。<br /><br />

                        <strong>第2条（費用分担）</strong><br />
                        ・家賃は各自口座振込、光熱費・ネットは人数割りで毎月精算。<br />
                        ・日用品はローテーションで購入し、LINEグループで報告。<br /><br />

                        <strong>第3条（来客・宿泊）</strong><br />
                        ・来客・宿泊は事前に全員へ連絡。<br />
                        ・異性の宿泊は不可、同性友人は月3回まで。<br /><br />

                        <strong>第4条（ルール変更・トラブル時）</strong><br />
                        ・ルール改定は全員合意。<br />
                        ・トラブル発生時は全員で速やかに話し合う。<br />
                        <strong>第5条　・・・</strong><br />
                        ・・・<br />
                    </Typography>
                </Paper>
                <Typography variant="body2" sx={{ color: '#757575', mb: 1 }}>
                    <strong>ポイント：</strong>「紙で掲示」＋「Googleドライブ等に保存」がおすすめ。入居者が変わるたびに見直そう！
                </Typography>
            </Box>

            {/* トラブルを防ぐためのコツ */}
            <Box id="troubles" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    トラブルを防ぐためのコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルールを作っても、<span style={{ color: '#1976d2' }}>コミュニケーション</span>や<span style={{ color: '#1976d2' }}>小さな配慮</span>がなければ意味がありません。<br />
                    ここでは円満なルームシェアに欠かせない「実践的コツ」をご紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <Group sx={{ mr: 1, color: '#1976d2', verticalAlign: 'middle' }} />
                                    定期的な「ルーム会議」
                                </Typography>
                                <Typography variant="body2">
                                    月1回など定期的に短時間でも集まり、困りごとや改善点を話し合う場を作ると、<strong>モヤモヤの蓄積を防げます</strong>。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <ChevronRight sx={{ mr: 1, color: '#43a047', verticalAlign: 'middle' }} />
                                    気になることは早めに伝える
                                </Typography>
                                <Typography variant="body2">
                                    「ちょっと困るな」と思った時、小さなことでもすぐ伝え合う習慣が大切。<strong>我慢せず、冷静に気持ちをシェア</strong>しましょう。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <InfoOutlined sx={{ mr: 1, color: '#fb8c00', verticalAlign: 'middle' }} />
                                    新メンバー加入時は再確認
                                </Typography>
                                <Typography variant="body2">
                                    途中から新しいメンバーが入る場合は、<strong>必ず全員でルールを確認し合意</strong>。<br />
                                    ルール説明の場を持つことでトラブル激減！
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <CheckCircle sx={{ mr: 1, color: '#1976d2', verticalAlign: 'middle' }} />
                                    「ありがとう」を忘れずに
                                </Typography>
                                <Typography variant="body2">
                                    掃除やゴミ出しなど、<strong>日々の小さな気遣い</strong>に感謝を伝える習慣が、関係性を良好に保つコツです。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ・円満なルームシェアのために
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは<span style={{ color: '#1976d2' }}>「他者との協力」</span>と<span style={{ color: '#1976d2' }}>「個人の尊重」</span>の絶妙なバランスが大切です。<br />
                    <strong>ルール作り</strong>は面倒に感じるかもしれませんが、<span style={{ background: '#e3f2fd', padding: '0 2px', borderRadius: 1 }}>「決めておいてよかった！」</span>と必ず思えるはず。<br />
                    ぜひ本記事のポイントを参考に、<Chip label="快適" color="primary" size="small" sx={{ mx: 0.5 }} />で<Chip label="安心" color="primary" size="small" sx={{ mx: 0.5 }} />なルームシェアライフをスタートさせてください！
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <img
                        src="/images/blog/happy-roommates_roomshare-rule-guide.jpg"
                        alt="笑顔で談笑するルームメイトたちがキッチンで協力して料理を作り、明るく清潔な空間で楽しいひとときを過ごしている。多文化な雰囲気と家庭的な温かみが感じられる。"
                        style={{
                            width: '100%',
                            maxHeight: 220,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #eee'
                        }}
                    />
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    本記事があなたのルームシェア生活のスタートダッシュや、既存メンバーとの関係改善のヒントになれば幸いです。
                </Typography>
                <Typography variant="body2" sx={{ color: '#757575' }}>
                    ルームシェアのルール作りでお悩みの方は、この記事をシェアしたり、SNSでご相談ください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-rule-guide',
    slug: 'roomshare-rule-guide',
    title: 'ルームシェアのルール作り完全ガイド：トラブルを未然に防ぐ10のポイント',
    excerpt: '必須ルール項目リスト 掃除・ゴミ出し分担 費用分担の決め方 来客・宿泊ルール ルール文書化の雛形',
    coverImage: '/images/blog/cover-roomshare-guide_roomshare-rule-guide.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目のテーブルと観葉植物がアクセントになり、安心感と快適さを感じさせる空間。',
    publishedAt: '2024-06-13T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '生活ノウハウ', 'トラブル防止'],
    tags: ['ルームシェア', 'ルール', '分担', '費用', '生活術'],
    author: {
        name: '佐藤 未来',
        avatar: '/images/avatars/sato-mirai.jpg',
        bio: 'ルームシェア運営歴10年以上。複数物件の管理経験と相談事例をもとに、実践的な暮らしの知恵を発信中。',
        profilePicture: '/images/avatars/sato-mirai.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;